import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { trackAnalyticsEvent } from '../lib/analytics'
import { useCurrency } from './CurrencyContext'

export interface CartItem {
  id: string
  productId: number
  productTitle: string
  sizeLabel: string
  unitPrice: number
  basePrice: number
  quantity: number
  image: string
  requiresReferences?: boolean
  currencyCode: string
}

type AddCartItemInput = Omit<CartItem, 'id' | 'currencyCode' | 'unitPrice'>

interface CartContextValue {
  items: CartItem[]
  totalQuantity: number
  subtotal: number
  addItem: (item: AddCartItemInput) => void
  removeItem: (id: string) => void
  clearCart: () => void
}

const CartContext = createContext<CartContextValue | undefined>(undefined)
const STORAGE_KEY = 'artist_cart_items'

const getInitialCart = (): CartItem[] => {
  if (typeof window === 'undefined') return []
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    const parsed = JSON.parse(stored) as Partial<CartItem>[]
    if (!Array.isArray(parsed)) return []
    return parsed.map((item, index) => ({
      id: item.id || `${Date.now()}-${index}`,
      productId: item.productId ?? 0,
      productTitle: item.productTitle || 'Artwork',
      sizeLabel: item.sizeLabel || '',
      unitPrice: Number.isFinite(item.unitPrice) ? Number(item.unitPrice) : 0,
      basePrice: Number.isFinite(item.basePrice) ? Number(item.basePrice) : Number(item.unitPrice) || 0,
      quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
      image: item.image || '',
      requiresReferences: Boolean(item.requiresReferences),
      currencyCode: item.currencyCode || 'EUR',
    }))
  } catch (_err) {
    return []
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(getInitialCart)
  const pendingCartEventRef = useRef<
    | null
    | { type: 'add'; item: AddCartItemInput }
    | { type: 'remove'; item: CartItem | null }
    | { type: 'clear'; count: number }
  >(null)
  const { convertPrice, currency, eurToUahRate } = useCurrency()
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  const addItem = (item: AddCartItemInput) => {
    pendingCartEventRef.current = { type: 'add', item }
    setItems((prev) => {
      const requiresReferences = Boolean(item.requiresReferences)
      const existingIndex = prev.findIndex(
        (entry) =>
          entry.productId === item.productId &&
          entry.sizeLabel === item.sizeLabel &&
          Boolean(entry.requiresReferences) === requiresReferences,
      )
      if (existingIndex !== -1) {
        const updated = [...prev]
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity,
          unitPrice: convertPrice(item.basePrice),
          basePrice: item.basePrice,
          currencyCode: currency.code,
        }
        return updated
      }
      const id =
        typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function'
          ? globalThis.crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`
      return [
        ...prev,
        {
          ...item,
          id,
          requiresReferences,
          basePrice: item.basePrice,
          unitPrice: convertPrice(item.basePrice),
          currencyCode: currency.code,
        },
      ]
    })
  }

  const removeItem = (id: string) => {
    const target = items.find((entry) => entry.id === id) || null
    pendingCartEventRef.current = { type: 'remove', item: target }
    setItems((prev) => prev.filter((item) => item.id !== id))
  }

  const clearCart = () => {
    if (items.length === 0) return
    pendingCartEventRef.current = { type: 'clear', count: items.length }
    setItems([])
  }

  const { totalQuantity, subtotal } = useMemo(() => {
    const quantity = items.reduce((sum, item) => sum + item.quantity, 0)
    const total = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
    return { totalQuantity: quantity, subtotal: total }
  }, [items])

  useEffect(() => {
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        unitPrice: convertPrice(item.basePrice),
        currencyCode: currency.code,
      })),
    )
  }, [convertPrice, currency.code, eurToUahRate])

  useEffect(() => {
    const pending = pendingCartEventRef.current
    if (!pending) return
    if (pending.type === 'add') {
      const added = items.find(
        (entry) =>
          entry.productId === pending.item.productId &&
          entry.sizeLabel === pending.item.sizeLabel &&
          Boolean(entry.requiresReferences) === Boolean(pending.item.requiresReferences),
      )
      trackAnalyticsEvent('cart_item_added', {
        metadata: {
          productId: pending.item.productId,
          sizeLabel: pending.item.sizeLabel,
          quantity: added?.quantity ?? pending.item.quantity,
          requiresReferences: pending.item.requiresReferences ? 'yes' : 'no',
        },
      })
    } else if (pending.type === 'remove' && pending.item) {
      trackAnalyticsEvent('cart_item_removed', {
        metadata: {
          productId: pending.item.productId,
          sizeLabel: pending.item.sizeLabel,
          quantity: pending.item.quantity,
        },
      })
    } else if (pending.type === 'clear' && pending.count > 0) {
      trackAnalyticsEvent('cart_cleared', {
        metadata: { removedCount: pending.count },
      })
    }
    pendingCartEventRef.current = null
  }, [items])

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, totalQuantity, subtotal }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
